const MAX_IMAGE_SIZE = 20 * 1024 * 1024; // 20MB
const MAX_IMAGES_PER_SESSION = 10;
const COMPRESSION_MAX_DIMENSION = 2048;
const JPEG_QUALITY_STEPS = [0.85, 0.78, 0.72, 0.66, 0.6, 0.54, 0.48, 0.42];
const DIMENSION_SCALE_STEPS = [1, 0.92, 0.84, 0.76, 0.68, 0.6, 0.52, 0.44, 0.36, 0.28];

export { MAX_IMAGE_SIZE, MAX_IMAGES_PER_SESSION };

export interface PreparedImageResult {
  file: File;
  originalSizeBytes: number;
  finalSizeBytes: number;
  wasCompressed: boolean;
}

/**
 * Read a File as base64 string (without the data: prefix)
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1] ?? '';
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Read a File as a data URL (for preview thumbnails)
 */
export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

function getFileExtension(name: string): string {
  const lastDot = name.lastIndexOf('.');
  return lastDot >= 0 ? name.slice(lastDot).toLowerCase() : '';
}

function normalizeMimeType(file: File): string {
  const explicitType = file.type?.toLowerCase();
  if (explicitType) {
    return explicitType;
  }

  const extension = getFileExtension(file.name);
  if (extension === '.png') return 'image/png';
  if (extension === '.gif') return 'image/gif';
  if (extension === '.jpg' || extension === '.jpeg') return 'image/jpeg';
  if (extension === '.heic') return 'image/heic';
  if (extension === '.heif') return 'image/heif';
  if (extension === '.webp') return 'image/webp';
  return '';
}

function isGifLike(file: File): boolean {
  const mimeType = normalizeMimeType(file);
  const extension = getFileExtension(file.name);
  return mimeType === 'image/gif' || extension === '.gif';
}

function isPngLike(file: File): boolean {
  const mimeType = normalizeMimeType(file);
  const extension = getFileExtension(file.name);
  return mimeType === 'image/png' || extension === '.png';
}

function isHeicLike(file: File): boolean {
  const mimeType = normalizeMimeType(file);
  const extension = getFileExtension(file.name);
  return (
    mimeType === 'image/heic' ||
    mimeType === 'image/heif' ||
    mimeType === 'image/heic-sequence' ||
    mimeType === 'image/heif-sequence' ||
    extension === '.heic' ||
    extension === '.heif'
  );
}

function renameFileExtension(name: string, nextExtension: string): string {
  const lastDot = name.lastIndexOf('.');
  const baseName = lastDot >= 0 ? name.slice(0, lastDot) : name;
  return `${baseName}${nextExtension}`;
}

function fitWithinMaxDimension(width: number, height: number) {
  if (width <= COMPRESSION_MAX_DIMENSION && height <= COMPRESSION_MAX_DIMENSION) {
    return { width, height };
  }

  const ratio = Math.min(COMPRESSION_MAX_DIMENSION / width, COMPRESSION_MAX_DIMENSION / height);
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image for compression.'));
    };

    img.src = url;
  });
}

function drawImageToCanvas(image: CanvasImageSource, width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas is not available in this browser.');
  }

  ctx.drawImage(image, 0, 0, width, height);
  return canvas;
}

function canvasToBlob(canvas: HTMLCanvasElement, mimeType: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Image compression failed.'));
          return;
        }
        resolve(blob);
      },
      mimeType,
      quality,
    );
  });
}

function canvasHasTransparency(canvas: HTMLCanvasElement): boolean {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return false;
  }

  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  for (let index = 3; index < data.length; index += 4) {
    if (data[index] < 255) {
      return true;
    }
  }

  return false;
}

function buildOutputFile(file: File, blob: Blob, mimeType: string): File {
  const nextName = mimeType === 'image/png' ? renameFileExtension(file.name, '.png') : renameFileExtension(file.name, '.jpg');
  return new File([blob], nextName, {
    type: mimeType,
    lastModified: file.lastModified,
  });
}

function resultFor(file: File, originalSizeBytes: number, wasCompressed: boolean): PreparedImageResult {
  return {
    file,
    originalSizeBytes,
    finalSizeBytes: file.size,
    wasCompressed,
  };
}

/**
 * Prepare an image file for upload. Large images are resized/compressed client-side
 * to stay under the upload limit.
 */
export async function prepareImageForUpload(file: File): Promise<PreparedImageResult> {
  const originalSizeBytes = file.size;

  if (isGifLike(file)) {
    if (file.size > MAX_IMAGE_SIZE) {
      throw new Error('GIFs over 20 MB cannot be compressed client-side yet. Try a smaller GIF.');
    }
    return resultFor(file, originalSizeBytes, false);
  }

  const shouldForceJpeg = isHeicLike(file);
  const isPng = isPngLike(file);
  if (!shouldForceJpeg && file.size <= MAX_IMAGE_SIZE) {
    return resultFor(file, originalSizeBytes, false);
  }

  const image = await loadImage(file);
  const baseDimensions = fitWithinMaxDimension(image.naturalWidth || image.width, image.naturalHeight || image.height);
  const baseCanvas = drawImageToCanvas(image, baseDimensions.width, baseDimensions.height);
  const shouldKeepPng = isPng && canvasHasTransparency(baseCanvas);
  const outputMimeType = shouldKeepPng ? 'image/png' : 'image/jpeg';

  let smallestBlob: Blob | null = null;
  let smallestMimeType = outputMimeType;

  for (const scale of DIMENSION_SCALE_STEPS) {
    const width = Math.max(1, Math.round(baseDimensions.width * scale));
    const height = Math.max(1, Math.round(baseDimensions.height * scale));
    const canvas = drawImageToCanvas(image, width, height);

    if (shouldKeepPng) {
      const pngBlob = await canvasToBlob(canvas, 'image/png');
      if (!smallestBlob || pngBlob.size < smallestBlob.size) {
        smallestBlob = pngBlob;
        smallestMimeType = 'image/png';
      }
      if (pngBlob.size <= MAX_IMAGE_SIZE) {
        return resultFor(buildOutputFile(file, pngBlob, 'image/png'), originalSizeBytes, true);
      }
      continue;
    }

    for (const quality of JPEG_QUALITY_STEPS) {
      const jpegBlob = await canvasToBlob(canvas, 'image/jpeg', quality);
      if (!smallestBlob || jpegBlob.size < smallestBlob.size) {
        smallestBlob = jpegBlob;
        smallestMimeType = 'image/jpeg';
      }
      if (jpegBlob.size <= MAX_IMAGE_SIZE) {
        return resultFor(buildOutputFile(file, jpegBlob, 'image/jpeg'), originalSizeBytes, true);
      }
    }
  }

  if (smallestBlob) {
    const attemptedFile = buildOutputFile(file, smallestBlob, smallestMimeType);
    throw new Error(
      `Image is still ${formatFileSize(attemptedFile.size)} after compression. Try cropping it before uploading.`,
    );
  }

  throw new Error('Failed to compress image. Please try another file.');
}

/**
 * Backwards-compatible helper returning only the processed file.
 */
export async function compressImage(file: File): Promise<File> {
  const result = await prepareImageForUpload(file);
  return result.file;
}

/**
 * Validate that a file is an image.
 */
export function isImageFile(file: File): boolean {
  const mimeType = normalizeMimeType(file);
  return mimeType.startsWith('image/');
}

/**
 * Generate a unique ID for attachments.
 */
export function generateAttachmentId(): string {
  return crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  if (bytes >= 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }

  return `${bytes} B`;
}
